import React, { forwardRef, ChangeEvent, useCallback, useState } from "react";
import cx from "classnames";
import debounce from "lodash/debounce";

import { ErrorOutlineOutlined } from "@material-ui/icons";

import { DATATEST } from "./constants";
import { useStyles } from "./input-field.styles";

type TTextFieldProps = {
    name: string;
    type: string;

    label?: string;

    defaultValue?: string;

    error?: string;
    placeholder?: string;
    helperText?: string;

    autoFocus?: boolean;
    isRequired?: boolean;
    disabled?: boolean;

    className?: string;
    inputClassName?: string;
    labelClassName?: string;
    errorClassName?: string;
    helperTextClassName?: string;
    delay?: number;

    startIcon?: React.ReactNode;

    inputProps?: any;
    labelProps?: any;
    errorProps?: any;
    helperTextProps?: any;

    onChange?: (value: string, e: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
};

export const InputField = forwardRef<HTMLInputElement, TTextFieldProps>((props, ref) => {
    const [state, setState] = useState({ fiedlValue: props.defaultValue || "" });
    const styles = useStyles();

    const containerClasses = cx(styles.container, props.className);
    const labelClasses = cx(styles.label, props.labelClassName);
    const inputClasses = cx(styles.input, props.inputClassName, {
        [styles.inputError]: props.error,
        [styles.hasStartIcon]: props.startIcon,
    });
    const errorClasses = cx(styles.error, props.errorClassName);
    const helperTextClasses = cx(styles.helperText, props.helperTextClassName);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedOnChange = useCallback(
        debounce((value: string, e: ChangeEvent<HTMLInputElement>) => {
            if (props.onChange) {
                props.onChange(value, e);
            }
        }, props.delay),
        [],
    );

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        setState({ fiedlValue: value });

        if (props.onChange) {
            if (props.delay) {
                debouncedOnChange(value, e);
            } else {
                props.onChange(value, e);
            }
        }
    };

    return (
        <div className={containerClasses}>
            {props.label && (
                <label htmlFor={props.name} className={labelClasses} data-test={DATATEST.LABEL} {...props.labelProps}>
                    {props.label} {props.isRequired && <span className={styles.asterix}>*</span>}
                </label>
            )}
            {props.startIcon ? (
                <div className={styles.startIcon} data-test={DATATEST.START_ICON_WRAPPER}>
                    {props.startIcon}
                </div>
            ) : null}
            <input
                id={props.name}
                name={props.name}
                value={state.fiedlValue}
                type={props.type}
                placeholder={props.placeholder}
                ref={ref}
                autoFocus={props.autoFocus}
                disabled={props.disabled}
                className={inputClasses}
                data-test={DATATEST.INPUT}
                onChange={handleChange}
                onBlur={props.onBlur}
                {...props.inputProps}
            />
            {props.error && (
                <span className={errorClasses} data-test={DATATEST.ERROR_MESSAGE} {...props.errorProps}>
                    <ErrorOutlineOutlined className={styles.errorIcon} data-test={DATATEST.ERROR_ICON} />
                    {props.error}
                </span>
            )}
            {props.helperText && (
                <span className={helperTextClasses} data-test={DATATEST.HELPER_MESSAGE} {...props.helperTextProps}>
                    {props.helperText}
                </span>
            )}
        </div>
    );
});
